import clsx from 'clsx'
import { useRouter } from 'expo-router'
import { useEffect, useState, type ReactNode } from 'react'

import { Chip } from '@/src/elements/chip'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import type { TNotificationApi } from '@/src/features/notification/api/types/notificationApi.types'
import type { TNewPrayerNotificationTypes } from '@/src/features/notification/models/notification.types'
import { useMarkNotificationAsReadMutation } from '@/src/features/notification/services/useMarkNotificationAsReadMutation'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useBottom } from '../../BottomContext'

const objectTypeIntentionTypeMap = {
  'arrival-intention': INTENTION_TYPE.ARRIVAL,
  'birthday-intention': INTENTION_TYPE.BIRTHDAY,
  intention: INTENTION_TYPE.INTENTION,
} as const satisfies Partial<Record<TNotificationApi['objectType'], INTENTION_TYPE>>

type TPrayerMiniPromptProps = {
  notifications: TNotificationApi<TNewPrayerNotificationTypes>[]
}

export const PrayerMiniPrompt = ({ notifications }: TPrayerMiniPromptProps): ReactNode => {
  const [promptState, setPromptState] = useState({ rendered: true, visible: true })
  const { t } = useTranslate()
  const { navigate } = useRouter()
  const { routes } = useRoutes()
  const { bottom } = useBottom()

  const { mutate: markAsRead } = useMarkNotificationAsReadMutation()

  useEffect(() => {
    setPromptState({ rendered: true, visible: true })
    const renderTimeout = setTimeout(() => setPromptState((p) => ({ ...p, rendered: true })), 5000)
    const visibilityTimeout = setTimeout(() => setPromptState((p) => ({ ...p, visible: false })), 4000)

    return () => {
      clearTimeout(renderTimeout)
      clearTimeout(visibilityTimeout)
    }
  }, [notifications])

  if (!promptState.rendered || notifications.length === 0) return null

  const handlePress = () => {
    const { id, objectId, objectType } = notifications[0]
    markAsRead(id)
    navigate(routes.intentionByIdPrayers(objectId, objectTypeIntentionTypeMap[objectType]))
  }

  const text =
    notifications.length > 1
      ? t('message.prayer.has-just-prayed-for-you.short-multiple', { count: notifications.length })
      : t('message.prayer.has-just-prayed-for-you.short-single', {
          contactName: notifications[0].payload.user.name || '_',
        })

  return (
    <Chip
      className={clsx('absolute left-4 z-50 mb-8 shadow-lg', !promptState.visible && 'opacity-0')}
      colors="primary"
      iconName={ICON.FIRE}
      variant="flat"
      text={text}
      onPress={handlePress}
      style={{ bottom }}
      testID={TEST_ID.PRAYER.PROMPT.MINI_PROMPT}
    />
  )
}
